// The passkey as a key-derivation device (WebAuthn `prf` extension).
//
// There is no relying-party server: the challenge is generated locally, the
// credential is discoverable, userVerification is required, and the only
// thing the vault ever uses is `prf.results.first` from an assertion. PRF is
// evaluated in get() — never trusted from create() — because Safari only
// reports `prf.enabled` at registration and only evaluates at assertion.
//
// RP id: `lately.org` on lately.org and its subdomains (the same passkey then
// works in a Safari tab, the Docked web app, the iOS Home Screen web app, and
// — via ASAuthorizationPlatformPublicKeyCredentialProvider(relyingPartyIdentifier:
// "lately.org") — the native apps, because webcredentials:lately.org is in
// every entitlement and the AASA lists 4KHF3H2A5W.org.lately.phi). Anywhere
// else (localhost, a preview host) the RP id is the page's own host, so
// development never mints lately.org passkeys.

export interface PasskeyRegistration {
  credentialId: Uint8Array;
  // What the authenticator said at create(); informational only.
  prfEnabled: boolean | null;
}

export interface PasskeyAssertion {
  credentialId: Uint8Array;
  // prf.results.first, or null when the authenticator/browser did not
  // evaluate the extension (no PRF support: the vault falls back to SK-only).
  prf: Uint8Array | null;
  // response.userHandle: the user.id the credential was registered with
  // (discoverable credentials return it). The vault records it so a later
  // "create a new passkey" registers under the same identity and thereby
  // replaces this one in the password manager instead of adding a second.
  userHandle: Uint8Array | null;
}

export interface PasskeyRegisterOptions {
  rpId: string;
  rpName: string;
  // Stable per user of the vault, so re-enrolling replaces the previous
  // passkey in the password manager instead of piling up new ones.
  userId: Uint8Array;
  userName: string;
  userDisplayName: string;
}

export interface PasskeyAssertOptions {
  rpId: string;
  // The PRF evaluation input (crypto.PRF_SALT_V1).
  salt: Uint8Array;
  // Restrict to the vault's own slots when known; empty means "any
  // discoverable credential for this RP" — the reuse-first enrolment path and
  // the unlock fallback when the listed ids no longer exist.
  allowCredentialIds?: Uint8Array[];
}

// The vault's view of WebAuthn. The real one wraps navigator.credentials; the
// tests use a deterministic fake.
export interface PrfAuthenticator {
  // WebAuthn (with a platform authenticator) is usable here at all.
  available(): Promise<boolean>;
  register(options: PasskeyRegisterOptions): Promise<PasskeyRegistration>;
  assert(options: PasskeyAssertOptions): Promise<PasskeyAssertion>;
}

export const LATELY_RP_ID = "lately.org";

export function rpIdFor(hostname: string): string {
  const host = hostname.toLowerCase();
  if (host === LATELY_RP_ID || host.endsWith(`.${LATELY_RP_ID}`)) {
    return LATELY_RP_ID;
  }
  return host;
}

export class PasskeyCancelledError extends Error {
  constructor(message = "The passkey request was cancelled.") {
    super(message);
    this.name = "PasskeyCancelledError";
  }
}

export class PasskeyUnsupportedError extends Error {
  constructor(message = "Passkeys are not available in this browser.") {
    super(message);
    this.name = "PasskeyUnsupportedError";
  }
}

function plain(bytes: Uint8Array): Uint8Array<ArrayBuffer> {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy;
}

function isUserCancel(cause: unknown): boolean {
  return (
    cause instanceof DOMException &&
    (cause.name === "NotAllowedError" || cause.name === "AbortError")
  );
}

// Reads prf.results.first off getClientExtensionResults(); tolerant of
// browsers that omit the extension entirely.
function prfFirst(credential: PublicKeyCredential): Uint8Array | null {
  const results = credential.getClientExtensionResults() as {
    prf?: { enabled?: boolean; results?: { first?: BufferSource } };
  };
  const first = results.prf?.results?.first;
  if (!first) {
    return null;
  }
  return first instanceof ArrayBuffer
    ? new Uint8Array(first)
    : new Uint8Array(first.buffer, first.byteOffset, first.byteLength);
}

export class WebAuthnPrfAuthenticator implements PrfAuthenticator {
  async available(): Promise<boolean> {
    if (
      typeof window === "undefined" ||
      typeof PublicKeyCredential === "undefined" ||
      !navigator.credentials ||
      typeof navigator.credentials.get !== "function"
    ) {
      return false;
    }
    // WebAuthn needs a secure context; the vault falls back to SK-only
    // elsewhere (dev over plain http on a LAN address, for instance).
    if (typeof isSecureContext !== "undefined" && !isSecureContext) {
      return false;
    }
    try {
      const platform = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
      return platform === true;
    } catch {
      return false;
    }
  }

  async register(options: PasskeyRegisterOptions): Promise<PasskeyRegistration> {
    let credential: Credential | null;
    try {
      credential = await navigator.credentials.create({
        publicKey: {
          challenge: crypto.getRandomValues(new Uint8Array(32)),
          rp: { id: options.rpId, name: options.rpName },
          user: {
            id: plain(options.userId),
            name: options.userName,
            displayName: options.userDisplayName,
          },
          pubKeyCredParams: [
            { type: "public-key", alg: -7 }, // ES256
            { type: "public-key", alg: -257 }, // RS256
          ],
          authenticatorSelection: {
            residentKey: "required",
            requireResidentKey: true,
            userVerification: "required",
          },
          timeout: 120_000,
          attestation: "none",
          extensions: { prf: {} },
        },
      });
    } catch (cause) {
      if (isUserCancel(cause)) {
        throw new PasskeyCancelledError();
      }
      if (cause instanceof DOMException && cause.name === "NotSupportedError") {
        throw new PasskeyUnsupportedError(cause.message);
      }
      throw cause;
    }
    if (!(credential instanceof PublicKeyCredential)) {
      throw new PasskeyUnsupportedError("The browser returned no passkey.");
    }
    const results = credential.getClientExtensionResults() as { prf?: { enabled?: boolean } };
    return {
      credentialId: new Uint8Array(credential.rawId),
      prfEnabled: typeof results.prf?.enabled === "boolean" ? results.prf.enabled : null,
    };
  }

  async assert(options: PasskeyAssertOptions): Promise<PasskeyAssertion> {
    let credential: Credential | null;
    try {
      credential = await navigator.credentials.get({
        publicKey: {
          challenge: crypto.getRandomValues(new Uint8Array(32)),
          rpId: options.rpId,
          allowCredentials: (options.allowCredentialIds ?? []).map((id) => ({
            type: "public-key" as const,
            id: plain(id),
          })),
          userVerification: "required",
          timeout: 120_000,
          extensions: { prf: { eval: { first: plain(options.salt) } } },
        },
      });
    } catch (cause) {
      if (isUserCancel(cause)) {
        throw new PasskeyCancelledError();
      }
      throw cause;
    }
    if (!(credential instanceof PublicKeyCredential)) {
      throw new PasskeyUnsupportedError("The browser returned no assertion.");
    }
    const response = credential.response as AuthenticatorAssertionResponse;
    const handle = response.userHandle;
    return {
      credentialId: new Uint8Array(credential.rawId),
      prf: prfFirst(credential),
      userHandle: handle && handle.byteLength > 0 ? new Uint8Array(handle) : null,
    };
  }
}
